import React, { Component, ErrorInfo, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import ErrorHandler from '../utils/errorHandler';
import { showAlert } from '../utils/alert';
import ErrorPage from '../pages/ErrorPage';
import { isChunkLoadError, reloadOnceForStaleChunk, isStaleChunkReloadPending } from '../utils/lazyWithRetry';
import { captureBoundaryError } from '../utils/sentry';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  /**
   * เมื่อค่านี้เปลี่ยน (เช่น เปลี่ยน route) boundary จะ reset hasError อัตโนมัติ
   * เพื่อให้ผู้ใช้ "ออก" จากหน้า error ได้ด้วยการ navigate (กลับหน้าหลัก/ย้อนกลับ)
   */
  resetKey?: string | number;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  /** true เมื่อสั่ง reload เพราะ chunk เก่าหายหลัง deploy — render ข้อความรอแทนหน้า error */
  reloading?: boolean;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // chunk หายหลัง deploy frontend (tab เก่าถือ index.html เก่า) → reload 1 ครั้งแทนโชว์หน้า 500
    // (user report Mr.Hop 2026-08-26) — ถ้า reload แล้วยังล้ม guard จะปล่อยให้แสดงหน้า error ตามปกติ
    if (isChunkLoadError(error) && reloadOnceForStaleChunk()) {
      this.setState({ error, errorInfo, reloading: true });
      return;
    }
    // กำลัง reload เพราะ chunk หายอยู่แล้ว (KRP-FRONTEND-3/8/9/D/E) — error ใด ๆ ในช่วงนี้มาจากหน้ารุ่นเก่า
    // ไม่ต้องรายงาน/แจ้งผู้ใช้ หน้าใหม่กำลังมาแทน
    if (isStaleChunkReloadPending()) {
      this.setState({ error, errorInfo, reloading: true });
      return;
    }

    // Log the error to an error reporting service
    console.error('Error caught by ErrorBoundary:', error, errorInfo);
    captureBoundaryError(error, errorInfo.componentStack);
    this.setState({ error, errorInfo });

    // Handle the error using our Enhanced Error Handler
    const formattedError = ErrorHandler.handleError(error, 'React Error Boundary');

    // Show user-friendly error message
    showAlert(
      formattedError.title,
      formattedError.message + '\n\nกรุณารีเฟรชหน้าเว็บหรือติดต่อทีมดูแลระบบ',
      'error'
    );

    // Log formatted error for debugging
    console.error('Formatted error:', formattedError);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    // BUG FIX: เดิม boundary render `<Navigate to="/error">` แต่ ErrorPage เป็นลูกของ boundary
    // เดียวกัน + hasError ไม่เคย reset → ErrorPage ไม่ถูก render → หน้า /error ว่างเปล่า ผู้ใช้ติดค้าง.
    // เมื่อ route เปลี่ยน (resetKey เปลี่ยน) ให้ล้าง error เพื่อ render เนื้อหาหน้าใหม่ตามปกติ.
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: undefined, errorInfo: undefined, reloading: false });
    }
  }

  render(): ReactNode {
    if (this.state.hasError) {
      // กำลัง reload เพราะ chunk เก่าหาย — โชว์ข้อความสั้นๆ แทนหน้า 500 ระหว่างรอหน้าใหม่
      // (ถ้า reload แล้วยังล้ม reloading จะเป็น false → ตกไปหน้า error ปกติ ไม่ค้าง)
      if (this.state.reloading) {
        return (
          <div className="p-8 text-center text-secondary-500">
            กำลังโหลดเวอร์ชันใหม่ของระบบ...
          </div>
        );
      }

      // ถ้ามี custom fallback ใช้ตามนั้น
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // มิฉะนั้น render หน้า error แบบ inline (ไม่ navigate ออกไป route อื่น)
      // → ปุ่ม "กลับหน้าหลัก / ย้อนกลับ" จะเปลี่ยน route → resetKey เปลี่ยน → boundary reset → ออกจาก error ได้
      return (
        <ErrorPage
          statusCode={500}
          title="เกิดข้อผิดพลาดในการแสดงผล"
          message="ระบบพบข้อผิดพลาดที่ไม่คาดคิดในการแสดงผลหน้านี้ กรุณากลับหน้าหลัก โหลดใหม่ หรือติดต่อทีมดูแลระบบ"
        />
      );
    }

    return this.props.children;
  }
}

/**
 * RouteAwareErrorBoundary — ห่อ ErrorBoundary แล้วป้อน pathname ปัจจุบันเป็น resetKey
 * (ต้องอยู่ภายใต้ <Router>) เพื่อให้ boundary reset เมื่อผู้ใช้ navigate ออกจากหน้า error
 */
export const RouteAwareErrorBoundary: React.FC<{ children: ReactNode; fallback?: ReactNode }> = ({
  children,
  fallback,
}) => {
  const location = useLocation();
  return (
    <ErrorBoundary resetKey={location.pathname} fallback={fallback}>
      {children}
    </ErrorBoundary>
  );
};

export default ErrorBoundary;
